import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchData } from '../../apiService';
import {
  IconButton,
  CircularProgress,
  Badge,
} from '@mui/material';
import ArrowForward from '@mui/icons-material/ArrowForward';
import RestaurantMenu from '@mui/icons-material/Restaurant';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Home = () => {
  const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
  const hasCreateTransactionPermission = permissions && permissions.includes('Create Transaction');

  const { id } = useParams();
  const [freedata, setFreeData] = useState([]);
  const [occupieddata, setOccupiedData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let intervalId;

    if (isAuthenticated) {
      fetchAllData();

      intervalId = setInterval(() => {
        fetchAllData();
      }, 30000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isAuthenticated, companyId, token, logout, id]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchItemData(), fetchTableData()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchItemData = async () => {
    try {
      setFreeData([]);
      const response = await fetchData('TableNameApi', `/FreeTable/${companyId}`, token);
      if (response.responseCode === '1111') {
        setFreeData(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching Table Data', error);
    }
  };

  const fetchTableData = async () => {
    try {
      setOccupiedData([]);
      const response = await fetchData('TableNameApi', `/OccupiedTable/${companyId}`, token);
      if (response.responseCode === '1111') {
        setOccupiedData(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching Table Data', error);
    }
  };

  const getTimeDuration = (kotTime) => {
    const currentTime = new Date();
    const [hours, minutes, seconds] = kotTime.split(':');
    const kotDate = new Date();
    kotDate.setHours(hours, minutes, seconds.split('.')[0]);

    const diffInMs = currentTime - kotDate;
    if (diffInMs < 0) {
      return 'Time not valid';
    }

    const diffInMinutes = Math.floor(diffInMs / 60000);
    const hoursDiff = Math.floor(diffInMinutes / 60);
    const minutesDiff = diffInMinutes % 60;

    return hoursDiff > 0 ? `${hoursDiff}h ${minutesDiff}m` : `${minutesDiff}m`;
  };

  return (
    <div className="home-wrapper">
      <style>
        {`
          .home-wrapper {
            min-height: 100vh;
            background: radial-gradient(circle at top left, #ffffff, #f1f5f9 40%, #e2e8f0 100%);
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
            color: #0f172a;
          }

          .home-container {
            max-width: 1400px;
            margin: 0 auto;
          }

          .two-column-layout {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 24px;
          }

          .section-container {
            background: rgba(255, 255, 255, 0.6);
            backdrop-filter: blur(12px);
            border-radius: 16px;
            padding: 20px;
            border: 1px solid rgba(255, 255, 255, 0.8);
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .section-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-bottom: 12px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          }

          .section-title {
            font-size: 16px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0;
            color: #1e293b;
            letter-spacing: 0.5px;
            text-transform: uppercase;
          }

          .stats-badge {
            background: rgba(0, 0, 0, 0.04);
            padding: 4px 10px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            color: #64748b;
            border: 1px solid rgba(0, 0, 0, 0.05);
          }

          .table-grid {
            display: grid;
            gap: 12px;
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }

          .card {
            background: rgba(255, 255, 255, 0.8);
            border-radius: 12px;
            padding: 14px;
            border: 1px solid rgba(255, 255, 255, 1);
            transition: all 0.2s ease;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            gap: 10px;
            position: relative;
            overflow: hidden;
            height: 100%;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
          }

          .card:hover {
            transform: translateY(-2px);
            background: #ffffff;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
          }

          .occupied-card {
            border-top: 3px solid #e11d48;
          }

          .occupied-card:hover {
            box-shadow: 0 8px 24px rgba(225, 29, 72, 0.15);
          }

          .available-card {
            border-top: 3px solid #059669;
          }

          .available-card:hover {
            box-shadow: 0 8px 24px rgba(5, 150, 105, 0.15);
          }

          .table-name {
            font-size: 15px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;
            color: #0f172a;
          }

          .info-row {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            color: #64748b;
          }

          .action-button {
            background: rgba(0, 0, 0, 0.02);
            border-radius: 8px;
            padding: 6px 10px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            transition: all 0.2s;
            margin-top: auto;
            border: 1px solid rgba(0, 0, 0, 0.05);
          }

          .action-button:hover {
            background: rgba(0, 0, 0, 0.05);
            border-color: rgba(0, 0, 0, 0.1);
          }

          .action-text {
            color: #475569;
            font-weight: 500;
            font-size: 12px;
          }

          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 50vh;
          }

          .empty-state {
            text-align: center;
            padding: 40px 20px;
            color: #94a3b8;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          /* Responsive Tweaks */
          @media (max-width: 900px) {
            .two-column-layout {
              grid-template-columns: 1fr;
              gap: 20px;
            }
          }

          @media (max-width: 600px) {
            .home-wrapper {
               padding: 12px;
            }
            .section-container {
              padding: 16px;
              border-radius: 14px;
            }
            .table-grid {
              grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
              gap: 10px;
            }
            .section-title {
              font-size: 14px;
            }
            .card {
               padding: 12px;
            }
            .table-name {
               font-size: 14px;
            }
            .info-row {
               font-size: 11px;
            }
            .action-text {
               font-size: 11px;
            }
          }
        `}
      </style>

      {loading ? (
        <div className="loading-container">
          <CircularProgress size={40} style={{ color: '#6366f1' }} />
        </div>
      ) : (
        <div className="home-container">
          <div className="two-column-layout">
            {/* Occupied Tables */}
            <div className="section-container">
              <div className="section-header">
                <h2 className="section-title">
                  <EventSeatIcon style={{ fontSize: 20, color: '#e11d48' }} />
                  Occupied
                </h2>
                <div className="stats-badge">{occupieddata.length} Occupied</div>
              </div>
              
              <div className="table-grid">
                {occupieddata && occupieddata.length > 0 ? (
                  occupieddata.map((item) => (
                    <div key={item.Id} className="card occupied-card">
                      <div className="table-name">
                        <TableRestaurantIcon style={{ fontSize: 18 }} />
                        {item.Name}
                      </div>
                      <div className="info-row">
                        <AccessTimeIcon style={{ fontSize: 14 }} />
                        <span>{getTimeDuration(item.KOTTime)} • #{item.KotNo}</span>
                      </div>
                      {hasCreateTransactionPermission && (
                        <Link to={`/kotEdit/${item.KotId}`} style={{ textDecoration: 'none', marginTop: 'auto' }}>
                          <div className="action-button">
                            <span className="action-text">View Details</span>
                            <IconButton size="small" style={{ padding: '2px' }}>
                              <ArrowForward style={{ color: '#475569', fontSize: '14px' }} />
                            </IconButton>
                          </div>
                        </Link>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <CheckCircleIcon style={{ fontSize: 40, opacity: 0.5 }} />
                    <p style={{ fontSize: 14, margin: 0 }}>No occupied tables</p>
                  </div>
                )}
              </div>
            </div>

            {/* Available Tables */}
            <div className="section-container">
              <div className="section-header">
                <h2 className="section-title">
                  <CheckCircleIcon style={{ fontSize: 20, color: '#059669' }} />
                  Available
                </h2>
                <div className="stats-badge">{freedata.length} Ready</div>
              </div>
              
              <div className="table-grid">
                {freedata && freedata.length > 0 ? (
                  freedata.map((item) => (
                    <div key={item.Id} className="card available-card">
                      <div className="table-name">
                        <TableRestaurantIcon style={{ fontSize: 18 }} />
                        {item.Name}
                      </div>
                      <div className="info-row">
                        <CheckCircleIcon style={{ fontSize: 14 }} />
                        <span>Ready for booking</span>
                      </div>
                      {hasCreateTransactionPermission && (
                        <Link to={`/kotCreate/${item.Id}`} style={{ textDecoration: 'none', marginTop: 'auto' }}>
                          <div className="action-button">
                            <span className="action-text">Book Table</span>
                            <IconButton size="small" style={{ padding: '2px' }}>
                              <ArrowForward style={{ color: '#475569', fontSize: '14px' }} />
                            </IconButton>
                          </div>
                        </Link>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <EventSeatIcon style={{ fontSize: 40, opacity: 0.5 }} />
                    <p style={{ fontSize: 14, margin: 0 }}>No tables available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
